/**
 * 팩 로더 (ADR-046 · RULES §1.15 PACK_SOURCE). 게임이 서비스의 공개 경로에서 발행본을 받는다.
 *
 *  - 도전장 링크가 버전을 **못 박으면** 그 버전만 쓴다(번들 → 캐시 → 네트워크). 다른 버전으로 대체하지 않는다 — 같은 판이어야 한다.
 *  - 버전이 없으면 `latest` 포인터를 본 뒤 그 버전을 받는다. 포인터·팩 각각 2.5초 안에 끝나야 하고 실패하면 캐시 → 번들.
 *  - 받은 팩은 `validatePack` 을 통과하고 계산한 `packHash` 가 포인터·`X-Pack-Hash` 와 같아야 한다. 아니면 버린다.
 *  - 번들(`pack-*.ts`)은 오프라인·서비스 장애의 폴백이자 첫 배포의 시드다.
 *
 * 네트워크·저장소는 `PackSource` 로 주입한다 — 엔진은 여전히 팩 **객체**만 받고, 테스트는 가짜 소스로 돈다.
 */
import type { Locale } from '../engine/alphabet';
import { localeOf } from '../engine/alphabet';
import { type ContentPack, packHash, validatePack } from '../engine/pack';
import { PACK_CORE_1_0_0 } from './pack-core-1.0.0';
import { PACK_EN_THEME_1_0_0 } from './pack-en-theme-1.0.0';
import { PACK_THEME_1_0_0 } from './pack-theme-1.0.0';

export const BUNDLED: readonly ContentPack[] = [
  PACK_CORE_1_0_0,
  PACK_THEME_1_0_0,
  PACK_EN_THEME_1_0_0,
];

/** 폴백 표시명 — 서비스 `/packs` 가 주는 label 이 우선이다 */
export const PACK_LABEL: Readonly<Record<string, string>> = {
  core: '아재개그',
  theme: '나라·도시·음식',
  'en-theme': 'Countries · Cities · Foods',
};

export const PACK_FETCH_TIMEOUT_MS = 2500;

/** 옛 링크 호환 — `?pack=1.0.0` 은 core 다 */
export function normalizePackId(id: string): string {
  return id === '1.0.0' ? 'core' : id;
}

export function bundledPack(id: string): ContentPack | null {
  const pid = normalizePackId(id);
  return BUNDLED.find((p) => p.id === pid) ?? null;
}

export interface FetchResult {
  readonly ok: boolean;
  readonly status: number;
  readonly json: unknown;
  readonly header: (name: string) => string | null;
}

export interface PackSource {
  /** '' 이면 같은 오리진. 앱 빌드는 서비스 도메인(`VITE_PACK_ORIGIN`) */
  readonly origin: string;
  fetchJson(url: string, timeoutMs: number): Promise<FetchResult>;
  cacheGet(key: string): string | null;
  cacheSet(key: string, value: string): void;
  /** 앱 다리(Room). 다리가 없으면 undefined, 있는데 비었으면 null */
  nativeGet(id: string, version: string): string | null | undefined;
  nativePut(id: string, version: string, hash: string, json: string): void;
}

export interface LoadedPack {
  readonly pack: ContentPack;
  readonly from: 'bundle' | 'cache' | 'network';
  /** 링크가 버전을 못 박았는가 */
  readonly pinned: boolean;
}

export interface PackListing {
  readonly id: string;
  readonly label: string;
  readonly kind: string;
  readonly locale: string;
  readonly version: string;
  readonly packHash: string;
}

const cacheKey = (id: string, version: string): string => `ajae.pack.${id}@${version}`;
const latestKey = (id: string): string => `ajae.pack.${id}.latest`;

/** JSON → 검증된 팩. 모양이 팩이 아니거나 규칙에 걸리면 null */
function parsePack(
  json: unknown,
  id: string,
  version: string,
  expectHash: string | null,
): ContentPack | null {
  if (typeof json !== 'object' || json === null) return null;
  const p = json as ContentPack;
  if (p.id !== id || p.version !== version || !Array.isArray(p.questions)) return null;
  try {
    validatePack(p);
  } catch {
    return null;
  }
  if (expectHash !== null && packHash(p) !== expectHash) return null;
  return p;
}

async function fetchVersion(
  src: PackSource,
  id: string,
  version: string,
  expectHash: string | null,
): Promise<ContentPack | null> {
  let res: FetchResult;
  try {
    res = await src.fetchJson(`${src.origin}/packs/${id}/${version}`, PACK_FETCH_TIMEOUT_MS);
  } catch {
    return null;
  }
  if (!res.ok) return null;
  const hash = expectHash ?? res.header('X-Pack-Hash');
  const pack = parsePack(res.json, id, version, hash);
  if (pack === null) return null;
  const text = JSON.stringify(pack);
  src.cacheSet(cacheKey(id, version), text);
  src.cacheSet(latestKey(id), JSON.stringify({ version, packHash: packHash(pack) }));
  src.nativePut(id, version, packHash(pack), text);
  return pack;
}

function fromCache(
  src: PackSource,
  id: string,
  version: string,
  expectHash: string | null,
): ContentPack | null {
  const native = src.nativeGet(id, version);
  const raw =
    native !== undefined && native !== null ? native : src.cacheGet(cacheKey(id, version));
  if (raw === null) return null;
  try {
    return parsePack(JSON.parse(raw), id, version, expectHash);
  } catch {
    return null;
  }
}

function cachedLatest(src: PackSource, id: string): { version: string; packHash: string } | null {
  const raw = src.cacheGet(latestKey(id));
  if (raw === null) return null;
  try {
    const v = JSON.parse(raw) as { version?: unknown; packHash?: unknown };
    return typeof v.version === 'string' && typeof v.packHash === 'string'
      ? { version: v.version, packHash: v.packHash }
      : null;
  } catch {
    return null;
  }
}

export class PackUnavailableError extends Error {
  constructor(
    readonly packId: string,
    readonly version: string | null,
  ) {
    super(
      version === null
        ? `팩 '${packId}' 을 받지 못했다`
        : `팩 '${packId}@${version}' 을 받지 못했다`,
    );
    this.name = 'PackUnavailableError';
  }
}

/**
 * 팩을 고른다. `pinnedVersion` 이 있으면(도전장) 그 버전만, 없으면 최신 → 캐시 → 번들.
 * 실패하면 `PackUnavailableError` — 호출부가 사람 언어로 안내한다.
 */
export async function loadPack(
  rawId: string,
  pinnedVersion: string | null,
  src: PackSource,
): Promise<LoadedPack> {
  const id = normalizePackId(rawId);
  const bundled = bundledPack(id);

  if (pinnedVersion !== null) {
    if (bundled !== null && bundled.version === pinnedVersion)
      return { pack: bundled, from: 'bundle', pinned: true };
    const cached = fromCache(src, id, pinnedVersion, null);
    if (cached !== null) return { pack: cached, from: 'cache', pinned: true };
    const net = await fetchVersion(src, id, pinnedVersion, null);
    if (net !== null) return { pack: net, from: 'network', pinned: true };
    throw new PackUnavailableError(id, pinnedVersion);
  }

  // 최신 포인터
  let pointer: { version: string; packHash: string } | null = null;
  try {
    const res = await src.fetchJson(`${src.origin}/packs/${id}/latest`, PACK_FETCH_TIMEOUT_MS);
    if (res.ok && typeof res.json === 'object' && res.json !== null) {
      const j = res.json as { version?: unknown; packHash?: unknown };
      if (typeof j.version === 'string' && typeof j.packHash === 'string') {
        pointer = { version: j.version, packHash: j.packHash };
      }
    }
  } catch {
    pointer = null;
  }
  if (pointer !== null) {
    if (
      bundled !== null &&
      bundled.version === pointer.version &&
      packHash(bundled) === pointer.packHash
    ) {
      src.cacheSet(latestKey(id), JSON.stringify(pointer));
      return { pack: bundled, from: 'bundle', pinned: false };
    }
    const cached = fromCache(src, id, pointer.version, pointer.packHash);
    if (cached !== null) {
      src.cacheSet(latestKey(id), JSON.stringify(pointer));
      return { pack: cached, from: 'cache', pinned: false };
    }
    const net = await fetchVersion(src, id, pointer.version, pointer.packHash);
    if (net !== null) return { pack: net, from: 'network', pinned: false };
  }
  // 포인터 실패 · 팩 실패 → 마지막으로 성공한 최신 캐시 → 번들
  const last = cachedLatest(src, id);
  if (last !== null) {
    const cached = fromCache(src, id, last.version, last.packHash);
    if (cached !== null) return { pack: cached, from: 'cache', pinned: false };
  }
  if (bundled !== null) return { pack: bundled, from: 'bundle', pinned: false };
  throw new PackUnavailableError(id, null);
}

export interface BootPack {
  readonly loaded: LoadedPack;
  /** 요청한 팩(링크가 못 박은 버전, 없으면 그 팩의 최신)을 받지 못했다 — 호출부가 도전장을 버리고 안내한다 */
  readonly unavailable: boolean;
}

/**
 * 부팅의 팩 고르기 — 못 박은 버전 → (실패하면) 그 팩의 최신 → 캐시 → 번들 → (그 팩이 어디에도 없으면) core.
 * 순서와 의미는 `loadPack` 을 차례로 부르는 것과 같다. 다만 뒤 단계를 **미리 같이 출발**시켜 서비스가 응답하지
 * 않을 때의 대기가 타임아웃의 합이 아니라 가장 긴 하나가 되게 한다. 앞 단계가 성공하면 뒤 결과는 버린다.
 *  - 핀이 번들 버전이면 최신은 출발시키지 않는다(네트워크 0회 · 실패할 수 없다).
 *  - 번들에 있는 팩이면 core 폴백은 출발시키지 않는다(최신이 번들에서 끝난다).
 */
export async function loadBootPack(
  rawId: string,
  pinnedVersion: string | null,
  src: PackSource,
): Promise<BootPack> {
  const id = normalizePackId(rawId);
  const bundled = bundledPack(id);
  const early = (go: boolean, load: () => Promise<LoadedPack>): (() => Promise<LoadedPack>) => {
    if (!go) return load;
    const p = load();
    void p.catch(() => undefined); // 쓰지 않게 된 폴백의 실패가 unhandled rejection 으로 새지 않게
    return () => p;
  };
  const latest = early(pinnedVersion === null || bundled?.version !== pinnedVersion, () =>
    loadPack(id, null, src),
  );
  const core = early(bundled === null, () => loadPack('core', null, src));
  let unavailable = false;
  if (pinnedVersion !== null) {
    try {
      return { loaded: await loadPack(id, pinnedVersion, src), unavailable };
    } catch {
      unavailable = true;
    }
  }
  try {
    return { loaded: await latest(), unavailable };
  } catch {
    return { loaded: await core(), unavailable: true };
  }
}

/** 타이틀의 팩 목록 — 서비스 `/packs?locale=` 가 우선, 실패하면 번들에서 같은 로케일만 */
export async function listPacks(locale: Locale, src: PackSource): Promise<PackListing[]> {
  try {
    const res = await src.fetchJson(`${src.origin}/packs?locale=${locale}`, PACK_FETCH_TIMEOUT_MS);
    if (res.ok && typeof res.json === 'object' && res.json !== null) {
      const rows = (res.json as { packs?: unknown }).packs;
      if (Array.isArray(rows) && rows.length > 0) {
        return rows.filter(
          (r): r is PackListing =>
            typeof r === 'object' && r !== null && typeof (r as PackListing).id === 'string',
        );
      }
    }
  } catch {
    // 번들 폴백
  }
  return BUNDLED.filter((p) => localeOf(p) === locale).map((p) => ({
    id: p.id,
    label: PACK_LABEL[p.id] ?? p.id,
    kind: p.kind ?? 'joke',
    locale: localeOf(p),
    version: p.version,
    packHash: packHash(p),
  }));
}

/** 브라우저 구현 — fetch + localStorage + (있으면) 안드로이드 다리 */
export function browserPackSource(origin: string): PackSource {
  const w = typeof window === 'undefined' ? undefined : window;
  const native = w?.AjaeNative;
  return {
    origin,
    async fetchJson(url, timeoutMs) {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), timeoutMs);
      try {
        const res = await fetch(url, { signal: ctl.signal, cache: 'default' });
        const json: unknown = res.ok ? await res.json() : null;
        return { ok: res.ok, status: res.status, json, header: (n) => res.headers.get(n) };
      } finally {
        clearTimeout(timer);
      }
    },
    cacheGet(key) {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    cacheSet(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch {
        // 용량·프라이빗 모드 — 캐시는 있으면 좋은 것
      }
    },
    nativeGet(id, version) {
      if (native?.getPack === undefined) return undefined;
      try {
        return native.getPack(id, version);
      } catch {
        return null;
      }
    },
    nativePut(id, version, hash, json) {
      try {
        native?.putPack?.(id, version, hash, json);
      } catch {
        // 다리 실패는 게임을 막지 않는다
      }
    },
  };
}

export { PACK_CORE_1_0_0, PACK_EN_THEME_1_0_0, PACK_THEME_1_0_0 };
